import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Stethoscope, Sparkles, ArrowLeft, Loader2, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContentIdeasPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    fetchDoctorAndIdeas();
  }, [doctorId]);

  const fetchDoctorAndIdeas = async () => {
    try {
      const [doctorRes, ideasRes] = await Promise.all([
        axios.get(`${API}/doctors/${doctorId}`),
        axios.get(`${API}/ideas/${doctorId}`),
      ]);
      setDoctor(doctorRes.data);
      setIdeas(ideasRes.data.ideas || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setInitialLoad(false);
    }
  };

  const generateIdeas = async () => {
    if (!doctor) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/content/ideas`, {
        doctor_id: doctorId,
        specialty: doctor.specialty,
        country: doctor.country,
        city: doctor.city,
        language: doctor.language_preference,
      });

      setIdeas(response.data.ideas || []);
      toast.success(`Generated ${response.data.count} new content ideas!`);
    } catch (error) {
      console.error("Error generating ideas:", error);
      toast.error(
        formatApiError(
          error.response?.data?.detail,
          "Failed to generate ideas",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectIdea = (idea) => {
    navigate(`/script/${doctorId}`, {
      state: {
        topic: idea.topic,
        format: idea.format,
      },
    });
  };

  if (initialLoad) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5C7063]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(`/dashboard/${doctorId}`)}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="ideas-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Stethoscope className="w-8 h-8 text-[#2C5F44]" strokeWidth={1.5} />
            <span
              className="text-2xl font-bold text-[#2C5F44]"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              Content Ideas
            </span>
          </div>
          <Button
            onClick={generateIdeas}
            disabled={loading}
            className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
            data-testid="ideas-generate-btn"
          >
            {loading ? (
              <>
                <Loader2
                  className="w-4 h-4 mr-2 animate-spin"
                  strokeWidth={1.5}
                />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Generate Ideas
              </>
            )}
          </Button>
          {ideas.length > 0 && (
            <Button
              onClick={() => navigate(`/batch/${doctorId}`)}
              variant="outline"
              className="rounded-full border-[#2C5F44]/20"
              data-testid="ideas-batch-btn"
            >
              <Zap className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Batch Generate
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {ideas.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-[#82A08B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles
                className="w-12 h-12 text-[#2C5F44]"
                strokeWidth={1.5}
              />
            </div>
            <h2
              className="text-3xl font-bold text-[#1C2A21] mb-4"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              No Content Ideas Yet
            </h2>
            <p className="text-lg text-[#5C7063] mb-8 max-w-2xl mx-auto">
              Click "Generate Ideas" to get 10 AI-powered video content ideas
              tailored to your specialty and location.
            </p>
            <Button
              onClick={generateIdeas}
              disabled={loading}
              className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full px-8 py-6 text-lg"
              data-testid="ideas-empty-generate-btn"
            >
              {loading ? "Generating..." : "Generate Ideas Now"}
            </Button>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <h1
                className="text-4xl font-bold text-[#1C2A21] mb-2"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                Your Content Ideas
              </h1>
              <p className="text-lg text-[#5C7063]">
                {doctor?.specialty} content for {doctor?.city},{" "}
                {doctor?.country}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {ideas.map((idea, index) => (
                <motion.div
                  key={idea.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white rounded-3xl p-8 card-hover border border-[#2C5F44]/10 cursor-pointer"
                  onClick={() => handleSelectIdea(idea)}
                  data-testid={`idea-card-${index}`}
                >
                  <div className="mb-4">
                    <span className="inline-block px-4 py-1 bg-[#82A08B]/20 text-[#2C5F44] rounded-full text-sm font-medium">
                      {idea.format}
                    </span>
                  </div>
                  <h3
                    className="text-xl font-bold text-[#1C2A21] mb-3"
                    style={{ fontFamily: "Nunito, sans-serif" }}
                  >
                    {idea.topic}
                  </h3>
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#5C7063] mb-1">
                      Hook:
                    </p>
                    <p className="text-[#1C2A21]">{idea.hook}</p>
                  </div>
                  <div className="pt-4 border-t border-[#2C5F44]/10">
                    <p className="text-sm text-[#5C7063] italic">
                      ✨ Why it works: {idea.why_it_works}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
